import React, { useState, useEffect, useContext } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, sp, rad, type, icBox } from "../../constants/styles";
import Ic from "../common/Ic";

// Same feature catalog as AdminPricing.js. Kept in sync manually for
// now — small enough that the duplication is cheaper than a shared
// module that both screens would have to import.
const ALL_FEATURES = [
  "basic_dashboard", "payments", "maintenance", "whatsapp", "leases",
  "service_persons", "agreements", "commercial", "reports", "csv_export",
  "analytics", "automation", "multi_user", "priority_support",
];
const FEATURE_LABELS = {
  basic_dashboard: "Dashboard", payments: "Rent Payments", maintenance: "Maintenance",
  whatsapp: "WhatsApp", leases: "Leases", service_persons: "Service Persons",
  agreements: "Agreements", commercial: "Commercial", reports: "Reports",
  csv_export: "CSV Export", analytics: "Analytics", automation: "Automation",
  multi_user: "Multi-User", priority_support: "Priority Support",
};

// All prices on the wire are paise. UI converts to/from rupees so
// admins can type 100 instead of 10000.
const toRupees = (p) => (p == null || p === "") ? "" : Math.round(p) / 100;
const toPaise = (r) => {
  if (r === "" || r == null) return null;
  const n = parseFloat(r);
  if (isNaN(n)) return 0;
  return Math.round(n * 100);
};
const formatRs = (paise) => `₹${(Math.round(paise) / 100).toLocaleString('en-IN')}`;

// `embedded` lets this render inside another admin page (the Pricing
// & Plans screen) instead of as its own standalone route. When
// embedded, the Dashboard back-link and page heading are skipped so
// the host screen's chrome stays in charge of navigation.
export default function AdminPerPropertyPlan({ embedded = false }) {
  const { h, go, flash } = useContext(Ctx);
  const [plan, setPlan] = useState(null);
  const [preview, setPreview] = useState([]);
  const [cliff, setCliff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  // Bulk migration: two-step confirm. First click loads preview
  // (count + sample). Second click executes irreversibly.
  const [migratePreview, setMigratePreview] = useState(null);
  const [migrateLoading, setMigrateLoading] = useState(false);
  const [migrateConfirming, setMigrateConfirming] = useState(false);

  const load = async () => {
    setLoading(true);
    const res = await api.adminGetPerPropertyPlans();
    if (res.success && res.plans?.length) {
      const p = res.plans[0];
      setPlan(p);
      setPreview(p.preview || []);
      setCliff(p.cliff || null);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const startEdit = () => {
    if (!plan) return;
    setForm({
      name: plan.name,
      enabled: !!plan.enabled,
      unit_price_monthly: toRupees(plan.unit_price_monthly),
      unit_price_yearly: toRupees(plan.unit_price_yearly),
      bulk_threshold: plan.bulk_threshold,
      bulk_unit_price_monthly: toRupees(plan.bulk_unit_price_monthly),
      bulk_unit_price_yearly: toRupees(plan.bulk_unit_price_yearly),
      bulk_mode: plan.bulk_mode,
      min_monthly: toRupees(plan.min_monthly),
      min_yearly: toRupees(plan.min_yearly),
      features: [...(plan.features || [])],
    });
    setEditing(true);
  };

  const toggleFeature = (f) => {
    setForm(prev => ({
      ...prev,
      features: (prev.features || []).includes(f)
        ? prev.features.filter(x => x !== f)
        : [...(prev.features || []), f],
    }));
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      name: form.name,
      enabled: !!form.enabled,
      unit_price_monthly: toPaise(form.unit_price_monthly),
      unit_price_yearly: toPaise(form.unit_price_yearly),
      bulk_threshold: parseInt(form.bulk_threshold, 10) || 0,
      bulk_unit_price_monthly: form.bulk_unit_price_monthly === "" ? null : toPaise(form.bulk_unit_price_monthly),
      bulk_unit_price_yearly: form.bulk_unit_price_yearly === "" ? null : toPaise(form.bulk_unit_price_yearly),
      bulk_mode: form.bulk_mode,
      min_monthly: toPaise(form.min_monthly),
      min_yearly: toPaise(form.min_yearly),
      features: form.features || [],
    };
    const res = await api.adminUpdatePerPropertyPlan(plan.id, payload);
    setSaving(false);
    if (res.success) {
      flash?.("Per-property plan saved");
      setPlan(res.plan);
      setPreview(res.plan.preview || []);
      setCliff(res.plan.cliff || null);
      setEditing(false);
    } else {
      flash?.(res.message || "Failed to save", "error");
    }
  };

  const loadMigratePreview = async () => {
    if (!plan) return;
    setMigrateLoading(true);
    const res = await api.adminPreviewMigrateAll(plan.id);
    setMigrateLoading(false);
    if (res.success) {
      setMigratePreview(res);
      setMigrateConfirming(true);
    } else {
      flash?.(res.message || "Could not load migration preview", "error");
    }
  };

  const executeMigrate = async () => {
    if (!plan) return;
    setMigrateLoading(true);
    const res = await api.adminMigrateAllToPerProperty(plan.id);
    setMigrateLoading(false);
    if (res.success) {
      flash?.(`Migrated ${res.migratedCount} orgs to ${plan.name}`);
      setMigrateConfirming(false);
      setMigratePreview(null);
    } else {
      flash?.(res.message || "Migration failed", "error");
    }
  };

  const toggleEnabled = async () => {
    if (!plan) return;
    setSaving(true);
    const res = await api.adminUpdatePerPropertyPlan(plan.id, { enabled: !plan.enabled });
    setSaving(false);
    if (res.success) {
      flash?.(res.plan.enabled
        ? "Per-property plan ENABLED"
        : "Per-property plan DISABLED");
      setPlan(res.plan);
      setPreview(res.plan.preview || []);
      setCliff(res.plan.cliff || null);
    } else {
      flash?.(res.message || "Failed to toggle", "error");
    }
  };

  const fieldS = {
    width: "100%", padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.sm,
    border: `1px solid ${h.bd}`, background: h.ib, color: h.tx,
    ...type.body, outline: "none", boxSizing: "border-box",
  };
  const labelS = { ...type.xs, color: h.ts, display: "block", marginBottom: sp.xs, fontWeight: 600 };

  if (loading) {
    return <div style={{ paddingTop: 40, textAlign: "center" }}>
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={h.ac} strokeWidth="2" style={{ animation: "spin 1s linear infinite" }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
    </div>;
  }

  if (!plan) {
    return <div style={{ paddingTop: embedded ? 0 : sp.lg }}>
      {!embedded && (
        <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", ...type.sm, marginBottom: sp.md }}>
          ← Dashboard
        </button>
      )}
      <div style={crd(h)}>
        <p style={{ color: h.tx }}>No per-property plan configured. The default seed row should appear after the next server restart.</p>
      </div>
    </div>;
  }

  // ── Edit form ────────────────────────────────────────────
  if (editing) {
    return <div style={{ paddingTop: sp.lg }} className="animate-fadeIn">
      <button onClick={() => setEditing(false)} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", ...type.sm, marginBottom: sp.md, display: "flex", alignItems: "center", gap: sp.xs }}>
        <Ic n="arrow-left" s={14} c={h.ac} /> Back
      </button>
      <h2 style={{ ...type.h2, color: h.tx, margin: `0 0 ${sp.lg}px` }}>Edit Per-Property Plan</h2>

      <div style={crd(h)}>
        <div style={{ marginBottom: sp.lg }}>
          <label style={labelS}>Plan Name</label>
          <input value={form.name || ""} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={fieldS} />
        </div>

        {/* ── Base pricing ──────────────────────────────── */}
        <div style={{ ...type.sm, color: h.tx, fontWeight: 700, marginBottom: sp.sm }}>Base unit price (per property)</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: sp.sm, marginBottom: sp.lg }}>
          <div>
            <label style={labelS}>Monthly (₹/prop)</label>
            <input type="number" min={0} value={form.unit_price_monthly} onChange={e => setForm(f => ({ ...f, unit_price_monthly: e.target.value }))} style={fieldS} />
          </div>
          <div>
            <label style={labelS}>Yearly (₹/prop)</label>
            <input type="number" min={0} value={form.unit_price_yearly} onChange={e => setForm(f => ({ ...f, unit_price_yearly: e.target.value }))} style={fieldS} />
            <div style={{ ...type.xs, color: h.tt, marginTop: 2 }}>
              {(() => {
                const m = parseFloat(form.unit_price_monthly) || 0;
                const y = parseFloat(form.unit_price_yearly) || 0;
                if (!m || !y) return null;
                const annualIfMonthly = m * 12;
                const savings = annualIfMonthly > 0 ? Math.round((1 - y / annualIfMonthly) * 100) : 0;
                if (savings > 0) return <span style={{ color: h.gn, fontWeight: 600 }}>{savings}% off vs monthly × 12</span>;
                if (savings < 0) return <span style={{ color: h.rd, fontWeight: 600 }}>{Math.abs(savings)}% MORE than monthly × 12</span>;
                return null;
              })()}
            </div>
          </div>
        </div>

        {/* ── Volume discount ───────────────────────────── */}
        <div style={{ ...type.sm, color: h.tx, fontWeight: 700, marginBottom: sp.sm }}>Volume discount (optional)</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: sp.sm, marginBottom: sp.sm }}>
          <div>
            <label style={labelS}>Threshold (properties)</label>
            <input type="number" min={0} value={form.bulk_threshold} onChange={e => setForm(f => ({ ...f, bulk_threshold: e.target.value }))} style={fieldS} />
            <div style={{ ...type.xs, color: h.tt, marginTop: 2 }}>0 = no volume discount</div>
          </div>
          <div>
            <label style={labelS}>Mode</label>
            <select value={form.bulk_mode} onChange={e => setForm(f => ({ ...f, bulk_mode: e.target.value }))}
              style={{ ...fieldS, appearance: "auto" }}>
              <option value="graduated">Graduated (no cliff)</option>
              <option value="flat">Flat (cheaper rate for ALL props)</option>
            </select>
            <div style={{ ...type.xs, color: h.tt, marginTop: 2, lineHeight: 1.4 }}>
              {form.bulk_mode === "flat"
                ? "Once count > threshold, every property bills at bulk rate."
                : "First N props at base rate, remainder at bulk rate."}
            </div>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: sp.sm, marginBottom: sp.lg }}>
          <div>
            <label style={labelS}>Bulk monthly (₹/prop)</label>
            <input type="number" min={0} value={form.bulk_unit_price_monthly} onChange={e => setForm(f => ({ ...f, bulk_unit_price_monthly: e.target.value }))} style={fieldS} />
          </div>
          <div>
            <label style={labelS}>Bulk yearly (₹/prop)</label>
            <input type="number" min={0} value={form.bulk_unit_price_yearly} onChange={e => setForm(f => ({ ...f, bulk_unit_price_yearly: e.target.value }))} style={fieldS} />
          </div>
        </div>

        {/* ── Minimum floor ─────────────────────────────── */}
        <div style={{ ...type.sm, color: h.tx, fontWeight: 700, marginBottom: sp.sm }}>Minimum bill (floor)</div>
        <div style={{ ...type.xs, color: h.tt, marginBottom: sp.sm, lineHeight: 1.4 }}>
          Below this, landlords still pay the floor — covers fixed costs (BSP, payment processor) on single-property accounts.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: sp.sm, marginBottom: sp.lg }}>
          <div>
            <label style={labelS}>Min monthly (₹)</label>
            <input type="number" min={0} value={form.min_monthly} onChange={e => setForm(f => ({ ...f, min_monthly: e.target.value }))} style={fieldS} />
          </div>
          <div>
            <label style={labelS}>Min yearly (₹)</label>
            <input type="number" min={0} value={form.min_yearly} onChange={e => setForm(f => ({ ...f, min_yearly: e.target.value }))} style={fieldS} />
          </div>
        </div>

        {/* ── Features ──────────────────────────────────── */}
        <div style={{ marginBottom: sp.lg }}>
          <label style={labelS}>Features included</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: sp.xs }}>
            {ALL_FEATURES.map(f => {
              const on = (form.features || []).includes(f);
              return <div key={f} onClick={() => toggleFeature(f)} style={{
                display: "flex", alignItems: "center", gap: sp.sm, padding: `${sp.xs}px ${sp.sm}px`,
                borderRadius: rad.sm, border: `1px solid ${on ? h.ac : h.bd}`,
                background: on ? `${h.ac}08` : "transparent", cursor: "pointer",
              }}>
                <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${on ? h.ac : h.bd}`, background: on ? h.ac : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {on && <span style={{ color: "#fff", fontSize: 10, fontWeight: 700 }}>{"✓"}</span>}
                </div>
                <span style={{ ...type.xs, color: h.tx }}>{FEATURE_LABELS[f] || f}</span>
              </div>;
            })}
          </div>
        </div>

        <button onClick={save} disabled={saving}
          style={{ width: "100%", padding: sp.md, borderRadius: rad.md, border: "none", background: h.ac, color: "#fff", ...type.sm, fontWeight: 600, cursor: "pointer", opacity: saving ? 0.6 : 1 }}>
          {saving ? "Saving..." : "Save Plan"}
        </button>
      </div>
    </div>;
  }

  // ── Read-only view ───────────────────────────────────────
  return <div style={{ paddingTop: embedded ? 0 : sp.lg }} className="animate-fadeIn">
    {!embedded && (
      <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", ...type.sm, marginBottom: sp.md, display: "flex", alignItems: "center", gap: sp.xs }}>
        <Ic n="arrow-left" s={14} c={h.ac} /> Dashboard
      </button>
    )}

    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: sp.lg }}>
      {!embedded && <h2 style={{ ...type.h2, color: h.tx, margin: 0 }}>Per-Property Plan</h2>}
      {/* When embedded, push the Edit button right with a spacer so
          the layout doesn't collapse. */}
      {embedded && <div />}
      <button onClick={startEdit} style={{ padding: `${sp.xs}px ${sp.md}px`, borderRadius: rad.sm, border: `1px solid ${h.ac}`, background: "transparent", color: h.ac, ...type.xs, fontWeight: 600, cursor: "pointer" }}>
        Edit Per-Property Plan
      </button>
    </div>

    {/* Enable / disable card */}
    <div style={{ ...crd(h), marginBottom: sp.md, borderLeft: `4px solid ${plan.enabled ? h.gn : h.bd}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: sp.md }}>
        <div style={icBox(plan.enabled ? h.gn : h.sc, 40)}>
          <span style={{ fontSize: 18 }}>{plan.enabled ? "🏠" : "💤"}</span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ ...type.sm, color: h.tx, fontWeight: 700 }}>
            {plan.name} — {plan.enabled ? "Enabled" : "Disabled"}
          </div>
          <div style={{ ...type.xs, color: h.sc, marginTop: 2 }}>
            {plan.enabled
              ? "Active — landlords on this plan are billed per property."
              : "Inactive — turn ON to start billing landlords per property."}
          </div>
        </div>
        <label style={{ position: "relative", display: "inline-block", width: 44, height: 24, cursor: saving ? "wait" : "pointer", flexShrink: 0 }}>
          <input type="checkbox" checked={!!plan.enabled} disabled={saving} onChange={toggleEnabled} style={{ opacity: 0, width: 0, height: 0 }} />
          <span style={{ position: "absolute", inset: 0, background: plan.enabled ? h.gn : "#ccc", borderRadius: 24, transition: "0.2s" }} />
          <span style={{ position: "absolute", top: 2, left: plan.enabled ? 22 : 2, width: 20, height: 20, background: "#fff", borderRadius: "50%", transition: "0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
        </label>
      </div>
    </div>

    {/* Cliff warning */}
    {cliff && (
      <div style={{ ...crd(h), background: `${h.rd}08`, borderLeft: `4px solid ${h.rd}`, marginBottom: sp.md }}>
        <div style={{ ...type.sm, color: h.rd, fontWeight: 700, marginBottom: sp.xs }}>⚠️ Pricing cliff detected</div>
        <div style={{ ...type.xs, color: h.tx, lineHeight: 1.5 }}>
          At {cliff.threshold} properties the bill is <strong>{formatRs(cliff.before_paise)}</strong>, but at {cliff.threshold + 1} properties it drops to <strong>{formatRs(cliff.after_paise)}</strong> — a saving of <strong>{formatRs(cliff.drop_paise)}</strong>. A landlord can "save money" by adding a property. Consider switching to <em>graduated</em> mode.
        </div>
      </div>
    )}

    {/* Bulk-migration card. Always rendered on the per-property
        view so the admin can find it; the button is disabled when
        the per-property plan isn't enabled (or the live pricing
        mode is still fixed-tier), with a clear inline hint
        explaining what to do. Migrating onto a disabled plan would
        instantly strip features from every migrated org, hence the
        gate. */}
    <div style={{
      ...crd(h),
      marginBottom: sp.md,
      borderLeft: `4px solid ${plan.enabled ? h.wn : h.bd}`,
      background: plan.enabled ? `${h.wn}05` : "transparent",
      opacity: plan.enabled ? 1 : 0.85,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: sp.md, marginBottom: sp.sm }}>
        <div style={icBox(plan.enabled ? h.wn : h.sc, 40)}>
          <span style={{ fontSize: 18 }}>{plan.enabled ? "🚀" : "🔒"}</span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ ...type.sm, color: h.tx, fontWeight: 700 }}>Migrate all paying landlords</div>
          <div style={{ ...type.xs, color: h.sc, marginTop: 2, lineHeight: 1.4 }}>
            Switch every org currently on Free/Starter/Growth/Pro to this per-property plan.
            Each org keeps their existing expiry date.
          </div>
        </div>
      </div>

      {!plan.enabled && (
        <div style={{
          ...type.xs, color: h.wn, fontWeight: 600,
          marginBottom: sp.sm, padding: `${sp.sm}px ${sp.sm + 2}px`,
          background: `${h.wn}10`, borderRadius: rad.sm,
          border: `1px dashed ${h.wn}40`,
          lineHeight: 1.5,
        }}>
          To enable migration, first turn ON the <strong>Per Property — Enabled</strong> toggle above (or switch the live pricing mode to <strong>Per-Property</strong>). Migration is blocked while the plan is disabled, so existing landlords don't suddenly lose feature access.
        </div>
      )}

      {!migrateConfirming && (
        <button
          onClick={loadMigratePreview}
          disabled={migrateLoading || !plan.enabled}
          style={{
            width: "100%", padding: sp.sm + 2, borderRadius: rad.sm,
            border: `1px solid ${plan.enabled ? h.wn : h.bd}`,
            background: "transparent",
            color: plan.enabled ? h.wn : h.sc,
            ...type.xs, fontWeight: 700,
            cursor: (migrateLoading || !plan.enabled) ? "not-allowed" : "pointer",
            opacity: migrateLoading ? 0.6 : (plan.enabled ? 1 : 0.6),
          }}>
          {migrateLoading
            ? "Loading preview..."
            : plan.enabled
              ? "Preview migration →"
              : "Enable plan to migrate"}
        </button>
      )}

        {migrateConfirming && migratePreview && (
          <div style={{ marginTop: sp.sm, paddingTop: sp.sm, borderTop: `1px dashed ${h.bd}` }}>
            {/* Zero-eligible case = either no paying orgs at all, or
                every paying org is already on per-property. The
                second case is the most common after a successful
                migration — frame it positively so the admin sees
                'all done' instead of 'something is skipping users'. */}
            {migratePreview.eligibleCount === 0 && migratePreview.skipped?.alreadyOnPp > 0 ? (
              <>
                <div style={{ ...type.sm, color: h.tx, marginBottom: sp.xs, display: "flex", alignItems: "center", gap: 6 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={h.gn} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span><strong>All {migratePreview.skipped.alreadyOnPp}</strong> paying {migratePreview.skipped.alreadyOnPp === 1 ? "org is" : "orgs are"} already on this plan.</span>
                </div>
                <div style={{ ...type.xs, color: h.sc, marginBottom: sp.sm, lineHeight: 1.5 }}>
                  Nothing to migrate — every paying landlord is already billed per property.
                </div>
              </>
            ) : (
              <>
                <div style={{ ...type.sm, color: h.tx, marginBottom: sp.xs }}>
                  This will move <strong style={{ color: h.wn }}>{migratePreview.eligibleCount}</strong> {migratePreview.eligibleCount === 1 ? "org" : "orgs"} to <strong>{plan.name}</strong>.
                </div>
                {migratePreview.skipped?.alreadyOnPp > 0 && (
                  <div style={{ ...type.xs, color: h.sc, marginBottom: sp.xs }}>
                    {migratePreview.skipped.alreadyOnPp} {migratePreview.skipped.alreadyOnPp === 1 ? "org is" : "orgs are"} already on this plan and won't be touched.
                  </div>
                )}
                {migratePreview.sampleNames?.length > 0 && (
                  <div style={{ ...type.xs, color: h.sc, marginBottom: sp.sm, lineHeight: 1.5 }}>
                    Examples: {migratePreview.sampleNames.join(", ")}
                    {migratePreview.eligibleCount > migratePreview.sampleNames.length && ` + ${migratePreview.eligibleCount - migratePreview.sampleNames.length} more`}
                  </div>
                )}
                <div style={{ ...type.xs, color: h.rd, marginBottom: sp.sm, fontWeight: 600 }}>
                  ⚠️ This cannot be undone in one click. To reverse, each org would need a separate plan change.
                </div>
              </>
            )}
            <div style={{ display: "flex", gap: sp.sm }}>
              <button
                onClick={() => { setMigrateConfirming(false); setMigratePreview(null); }}
                disabled={migrateLoading}
                style={{
                  flex: 1, padding: sp.sm + 2, borderRadius: rad.sm,
                  border: `1px solid ${h.bd}`, background: "transparent",
                  color: h.ts, ...type.xs, fontWeight: 600, cursor: "pointer",
                }}>
                Cancel
              </button>
              <button
                onClick={executeMigrate}
                disabled={migrateLoading || migratePreview.eligibleCount === 0}
                style={{
                  flex: 2, padding: sp.sm + 2, borderRadius: rad.sm,
                  border: "none", background: h.wn, color: "#fff",
                  ...type.xs, fontWeight: 700, cursor: "pointer",
                  opacity: migrateLoading ? 0.6 : (migratePreview.eligibleCount === 0 ? 0.4 : 1),
                }}>
                {migrateLoading
                  ? "Migrating..."
                  : migratePreview.eligibleCount === 0
                    ? "Nothing to migrate"
                    : `Migrate ${migratePreview.eligibleCount} org${migratePreview.eligibleCount === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        )}
      </div>

    {/* Pricing summary */}
    <div style={{ ...crd(h), marginBottom: sp.md }}>
      <div style={{ ...type.sm, color: h.tx, fontWeight: 700, marginBottom: sp.sm }}>Pricing structure</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: sp.sm, marginBottom: sp.md }}>
        <div style={{ background: h.ib, padding: sp.sm, borderRadius: rad.sm }}>
          <div style={{ ...type.xs, color: h.sc, marginBottom: 2 }}>BASE / MONTHLY</div>
          <div style={{ ...type.h3, color: h.tx }}>{formatRs(plan.unit_price_monthly)}<span style={{ ...type.xs, color: h.sc, fontWeight: 400 }}> /prop</span></div>
        </div>
        <div style={{ background: h.ib, padding: sp.sm, borderRadius: rad.sm }}>
          <div style={{ ...type.xs, color: h.sc, marginBottom: 2 }}>BASE / YEARLY</div>
          <div style={{ ...type.h3, color: h.tx }}>{formatRs(plan.unit_price_yearly)}<span style={{ ...type.xs, color: h.sc, fontWeight: 400 }}> /prop</span></div>
        </div>
      </div>
      {plan.bulk_threshold > 0 && (
        <>
          <div style={{ ...type.xs, color: h.sc, marginBottom: sp.xs, fontWeight: 600 }}>
            Volume discount above {plan.bulk_threshold} properties — mode: <span style={{ color: h.ac }}>{plan.bulk_mode}</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: sp.sm, marginBottom: sp.md }}>
            <div style={{ background: `${h.gn}10`, padding: sp.sm, borderRadius: rad.sm }}>
              <div style={{ ...type.xs, color: h.sc, marginBottom: 2 }}>BULK / MONTHLY</div>
              <div style={{ ...type.h3, color: h.gn }}>{formatRs(plan.bulk_unit_price_monthly || 0)}<span style={{ ...type.xs, color: h.sc, fontWeight: 400 }}> /prop</span></div>
            </div>
            <div style={{ background: `${h.gn}10`, padding: sp.sm, borderRadius: rad.sm }}>
              <div style={{ ...type.xs, color: h.sc, marginBottom: 2 }}>BULK / YEARLY</div>
              <div style={{ ...type.h3, color: h.gn }}>{formatRs(plan.bulk_unit_price_yearly || 0)}<span style={{ ...type.xs, color: h.sc, fontWeight: 400 }}> /prop</span></div>
            </div>
          </div>
        </>
      )}
      <div style={{ ...type.xs, color: h.sc }}>
        Min bill: {formatRs(plan.min_monthly)}/mo · {formatRs(plan.min_yearly)}/yr
      </div>
    </div>

    {/* Preview table */}
    <div style={{ ...crd(h), marginBottom: sp.md }}>
      <div style={{ ...type.sm, color: h.tx, fontWeight: 700, marginBottom: sp.sm }}>Bill preview</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "6px 8px", color: h.sc, borderBottom: `1px solid ${h.bd}`, fontWeight: 700 }}>PROPERTIES</th>
              <th style={{ textAlign: "right", padding: "6px 8px", color: h.sc, borderBottom: `1px solid ${h.bd}`, fontWeight: 700 }}>MONTHLY</th>
              <th style={{ textAlign: "right", padding: "6px 8px", color: h.sc, borderBottom: `1px solid ${h.bd}`, fontWeight: 700 }}>YEARLY</th>
              <th style={{ textAlign: "right", padding: "6px 8px", color: h.sc, borderBottom: `1px solid ${h.bd}`, fontWeight: 700 }}>EFFECTIVE /PROP/MO</th>
            </tr>
          </thead>
          <tbody>
            {preview.map((row, i) => {
              const effective = row.properties > 0 ? Math.round(row.monthly_paise / row.properties) : 0;
              const isCliff = cliff && row.properties === cliff.threshold + 1;
              return <tr key={row.properties} style={{ background: i % 2 === 0 ? "transparent" : h.ib }}>
                <td style={{ padding: "6px 8px", color: h.tx, borderBottom: `1px solid ${h.bd}40` }}>{row.properties}</td>
                <td style={{ padding: "6px 8px", color: h.tx, textAlign: "right", borderBottom: `1px solid ${h.bd}40`, fontWeight: 600 }}>{formatRs(row.monthly_paise)}{isCliff && <span style={{ color: h.rd, marginLeft: 4 }} title="Cheaper than threshold!">⚠</span>}</td>
                <td style={{ padding: "6px 8px", color: h.tx, textAlign: "right", borderBottom: `1px solid ${h.bd}40`, fontWeight: 600 }}>{formatRs(row.yearly_paise)}</td>
                <td style={{ padding: "6px 8px", color: h.sc, textAlign: "right", borderBottom: `1px solid ${h.bd}40` }}>{formatRs(effective)}</td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>
    </div>

    {/* Features list */}
    <div style={crd(h)}>
      <div style={{ ...type.sm, color: h.tx, fontWeight: 700, marginBottom: sp.sm }}>Features included ({(plan.features || []).length})</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: sp.xs }}>
        {(plan.features || []).map(f => (
          <span key={f} style={{ background: `${h.ac}10`, color: h.ac, padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
            {FEATURE_LABELS[f] || f}
          </span>
        ))}
      </div>
    </div>
  </div>;
}
